import React, { useLayoutEffect, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import { Box } from '../Box/Box';
import { styled } from '../../styles';
import { BpDesignSystem } from '../../types';
import useClientRect from '../../utils/useClientRect';

export type MouseParallaxIconProps = BpDesignSystem.PropsWithChildren<{
  backgroundImgUrl: string;
  foregroundImgUrl: string;
  height?: number;
  width?: number;
}>;

export const MouseParallaxIcon: React.FC<MouseParallaxIconProps> = ({
  backgroundImgUrl,
  foregroundImgUrl,
  height = 150,
  width = 300,
  ...rest
}) => {
  const [XY, setXY] = useState({ x: 0, y: 0 });
  const [rect, ref] = useClientRect();
  const [springProps, set] = useSpring<{ xy: [number, number]; opacity: number }>(() => ({
    xy: [0, 0],
    opacity: 1,
    from: { opacity: 0 },
    config: { mass: 10, tension: 550, friction: 140 }
  }));

  const calc = (x: number, y: number): [number, number] => [x - XY.x, y - XY.y];
  const trans1 = (x: any, y: any) => `translate3d(${x / 35}px,${y / 35}px,0)`;
  const trans2 = (x: any, y: any) => `translate3d(${x / 3}px,${y / 3}px,0)`;

  useLayoutEffect(() => {
    if (!rect) return;
    setXY({ x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 });
  }, [rect]);

  return (
    <StyledDiv
      $height={height}
      $width={width}
      onMouseMove={({ clientX: x, clientY: y }) => set({ xy: calc(x, y) })}
      onMouseLeave={() => set({ xy: calc(XY.x, XY.y) })}
      ref={ref}
      {...rest}
    >
      <animated.img className="bg" src={backgroundImgUrl} style={{ transform: springProps.xy.to(trans1 as any) }} />
      <animated.img
        className="fg"
        src={foregroundImgUrl}
        style={{ opacity: springProps.opacity, transform: springProps.xy.to(trans2 as any) }}
      />
    </StyledDiv>
  );
};

const StyledDiv = styled(Box)<{ $height: number; $width: number }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  height: ${({ $height }) => $height}px;
  width: ${({ $width }) => $width}px;

  .bg {
    width: ${({ $width }) => $width}px;
    height: ${({ $height }) => $height}px;
    top: 0;
    left: 0;
    position: absolute;
  }
  .fg {
    position: absolute;
    height: ${({ $height }) => $height / 2}px;
  }
`;
